/**
 * Painel lateral (desktop), flutuando sobre a borda esquerda do mapa, colado
 * ao trilho: abrir ou recolher não muda o tamanho do mapa nem desloca a imagem
 * (antes era uma coluna da grade e empurrava o mapa 340 px). Mostra o
 * painel da categoria ativa no trilho; o cabeçalho traz o nome da categoria,
 * o interruptor da camada (o mesmo do trilho), o status das imagens e o
 * botão de recolher.
 */
import clsx from 'clsx';
import { PanelLeftClose } from 'lucide-react';
import { SIDEBAR_WIDTH } from '../map/viewControl';
import { useUiStore } from '../state/ui';
import { LatestStatus } from './LatestStatus';
import { getPanel, type PanelDef } from './panels';
import { PanelContent } from './PanelTabs';
import { IconButton, Switch } from './primitives';
import { DEFAULT_TONE, TONE_ICON } from './tone';

/** Título do painel com o interruptor da camada ao lado. */
function PanelTitle(props: { def: PanelDef | undefined }) {
  const { def } = props;
  // O cabeçalho remonta ao trocar de aba (chave = id), então a ordem dos hooks não muda.
  const on = def?.layer?.useOn() ?? false;
  const Icon = def?.icon;
  return (
    <>
      {Icon && <Icon className={clsx('size-4 shrink-0', on ? TONE_ICON[def?.tone ?? DEFAULT_TONE] : 'text-ink-400')} aria-hidden />}
      <h2 className="min-w-0 truncate text-sm font-semibold text-white">{def?.label ?? 'Camadas'}</h2>
      {def?.layer && (
        <Switch checked={on} onChange={def.layer.setOn} label={`${def.label}: ${on ? 'desligar' : 'ligar'} camada`} />
      )}
    </>
  );
}

export function Sidebar() {
  const set = useUiStore((s) => s.set);
  const tab = useUiStore((s) => s.tab);
  const def = getPanel(tab);
  return (
    <aside
      data-painel-lateral=""
      className="absolute inset-y-0 left-0 z-30 flex min-h-0 flex-col border-r border-ink-700/80 bg-ink-900 shadow-[8px_0_24px_-12px_rgba(0,0,0,0.6)]"
      style={{ width: SIDEBAR_WIDTH }}
      aria-label="Painel de camadas"
    >
      <div className="flex items-center gap-2 border-b border-ink-700/70 px-3 py-2">
        <PanelTitle key={def?.id ?? ''} def={def} />
        <div className="min-w-0 flex-1">
          <LatestStatus variant="compact" className="ml-auto" />
        </div>
        <IconButton icon={PanelLeftClose} label="Recolher painel" size="sm" onClick={() => set({ sidebarOpen: false })} />
      </div>
      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
        <PanelContent />
      </div>
    </aside>
  );
}
